import React from "react";
import _ from "lodash";
import cx from "classnames";
import getDayRangeString from "../../../utils/day-range-string";
import Chart from "../../../parts/chart";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";

const {
  CUSTOM_METRIC_DATA_SERIES,
  PERIOD_FORMATS,
  DATE_FORMATS: { DATE_PICKER_FORMAT },
} = require("../../../constants");

const PERIOD_OPTIONS = {
  day: [{ text: "Daily", value: "day" }],
  week: [{ text: "Weekly", value: "week" }],
  month: [{ text: "Monthly", value: "month" }],
};

function View(props, { locals }) {
  const { query, snapshot, momentTZ } = locals;
  _.defaults(query, {
    units: locals.units,
  });
  const selectedUnits = query.units;
  const selectedPeriod = (query.period = _.get(snapshot, "data.period"));
  const editCustomMetric = _.get(snapshot, "data.editCustomMetric");
  const viewCustomMetric = _.get(snapshot, "data.viewCustomMetric");
  const selectedStartDate = momentTZ(
    _.get(snapshot, "data.start"),
    DATE_PICKER_FORMAT
  );
  const activeID = _.get(editCustomMetric, "_id");
  const activeName = _.get(editCustomMetric, "name", "N/A");
  const chartKeys = _.get(viewCustomMetric, "keys");
  const chartLabels = _.map(
    chartKeys,
    (i) => `${PERIOD_FORMATS[selectedPeriod]}${Number(i) + 1}`
  );
  const chartWidth = `${_.size(chartKeys) * 40}px`;
  const barRelation = _.get(viewCustomMetric, "barRelation");
  const barChartGroup = _.find(CUSTOM_METRIC_DATA_SERIES, {
    value: barRelation,
  });
  const lineRelation = _.get(viewCustomMetric, "lineRelation");
  const lineChartGroup = _.find(CUSTOM_METRIC_DATA_SERIES, {
    value: lineRelation,
  });
  const barChartAxisLabel = _.get(barChartGroup, "title");
  const lineChartAxisLabel = _.get(lineChartGroup, "title");
  const barChartSeries = buildCharts(
    _.get(viewCustomMetric, "bars"),
    chartKeys,
    barChartGroup,
    locals
  );
  const lineChartSeries = buildCharts(
    _.get(viewCustomMetric, "lines"),
    chartKeys,
    lineChartGroup,
    locals
  );
  const hasBarChartData = !_(barChartSeries).flatMap("data").isEmpty();
  const hasLineChartData = !_(lineChartSeries).flatMap("data").isEmpty();
  const barChartReducer = _.endsWith(
    _.get(editCustomMetric, "data.bar.series"),
    "-total"
  )
    ? _.sum
    : _.max;
  const lineChartReducer = _.endsWith(
    _.get(editCustomMetric, "data.line.series"),
    "-total"
  )
    ? _.sum
    : _.max;

  let maxLineChartValue = _.max(
    _.zipWith.apply(
      _,
      _.map(lineChartSeries, "data").concat(argsToArray(lineChartReducer))
    )
  );
  let maxBarChartValue = _.max(
    _.zipWith.apply(
      _,
      _.map(barChartSeries, "data").concat(argsToArray(barChartReducer))
    )
  );

  if (_.get(barChartGroup, "value") === _.get(lineChartGroup, "value")) {
    maxLineChartValue = maxBarChartValue = Math.max(
      maxLineChartValue,
      maxBarChartValue
    );
  }

  return (
    <div className="body metrics">
      <h1 className="alt">Custom Metric Report: {activeName}</h1>
      <p></p>

      <div className="Chart-Frame bar-chart">
        <div className="head">
          <div className="right-side">
            <div className="group">
              <ToggleLinksInput
                name="period"
                options={PERIOD_OPTIONS[selectedPeriod]}
              />
            </div>

            {Boolean(barRelation === "volume" || lineRelation === "volume") && (
              <div className="group">
                <ToggleLinksInput
                  name="units"
                  options={[
                    { text: "kg", value: "metric" },
                    { text: "lbs", value: "imperial" },
                  ]}
                />
              </div>
            )}
          </div>
        </div>

        <div className="body">
          {(hasBarChartData || hasLineChartData) && (
            <div>
              <div className="legend">
                {_.map(barChartSeries, ({ className, title }) => {
                  return (
                    <div className={`bar series ${className}`}>{title}</div>
                  );
                })}

                {_.map(lineChartSeries, ({ className, title }) => {
                  return (
                    <div className={`line series ${className}`}>{title}</div>
                  );
                })}
              </div>
              <div className="axis-labels">
                <div className="left">{barChartAxisLabel}</div>
                <div className="right">{lineChartAxisLabel}</div>
              </div>
              <div className="chart-fullsize">
                <div className="labels">
                  <Chart
                    type="Bar"
                    only="label"
                    data={{ series: barChartSeries }}
                    options={{
                      width: "60px",
                      height: "380px",
                      stackBars: true,
                      axisX: { showGrid: false, showLabel: false },
                      axisY: {
                        showGrid: false,
                        showLabel: true,
                        onlyInteger: true,
                        scaleMinSpace: 30,
                        labelInterpolationFnc: _.get(
                          barChartGroup,
                          "labelMap",
                          _.noop
                        ),
                      },
                      low: 0,
                      high: maxBarChartValue,
                    }}
                  />

                  <Chart
                    type="Line"
                    only="label"
                    data={{ series: lineChartSeries }}
                    options={{
                      width: "60px",
                      height: "380px",
                      axisX: { showGrid: false, showLabel: false },
                      axisY: {
                        stretch: true,
                        showGrid: false,
                        showLabel: true,
                        scaleMinSpace: 30,
                        labelInterpolationFnc: _.get(
                          lineChartGroup,
                          "labelMap",
                          _.noop
                        ),
                      },
                      low: 0,
                      high: maxLineChartValue,
                    }}
                  />
                </div>

                <div className="data">
                  <Chart
                    type="Bar"
                    data={{
                      labels: chartLabels,
                      series: barChartSeries,
                    }}
                    options={{
                      width: chartWidth,
                      height: "380px",
                      stackBars: true,
                      chartPadding: { left: 35, right: 75 },
                      axisX: { showGrid: false, showLabel: true },
                      axisY: {
                        scaleMinSpace: 30,
                        showGrid: true,
                        showLabel: false,
                        scaleMinSpace: 30,
                      },
                      low: 0,
                      high: maxBarChartValue,
                    }}
                    tooltip={(item, i) => {
                      const key = Number(chartKeys[i]);
                      const date = selectedStartDate
                        .clone()
                        .add(key, selectedPeriod);
                      const dayRange = getDayRangeString(
                        [
                          date,
                          date
                            .clone()
                            .add(1, selectedPeriod)
                            .subtract(1, "day"),
                        ],
                        query
                      );
                      const barTotal =
                        _.size(barChartSeries) >= 2 &&
                        _.sumBy(barChartSeries, `data.${i}`);
                      return (
                        <div className="tooltip" style={{ left: item.x }}>
                          <div className="details">
                            <div>
                              <b>{dayRange}</b>
                            </div>
                            {_.map(barChartSeries, ({ title, data }) => {
                              if (!data[i]) return;
                              return (
                                <div key={title}>
                                  {title}{" "}
                                  <b>
                                    {barChartGroup.tooltipMap(
                                      data[i],
                                      selectedUnits,
                                      locals
                                    )}{" "}
                                    {barTotal &&
                                      ` (${_.round(
                                        (data[i] / barTotal) * 100,
                                        1
                                      )}%)`}
                                  </b>
                                </div>
                              );
                            })}
                            {barTotal && (
                              <div>
                                Total {barChartGroup.title}{" "}
                                <b>
                                  {barChartGroup.tooltipMap(
                                    barTotal,
                                    selectedUnits,
                                    locals
                                  )}
                                </b>
                              </div>
                            )}
                            {_.map(lineChartSeries, ({ title, data }) => {
                              if (!data[i]) return;
                              return (
                                <div key={title}>
                                  {title}{" "}
                                  <b>
                                    {lineChartGroup.tooltipMap(
                                      data[i],
                                      selectedUnits,
                                      locals
                                    )}
                                  </b>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }}
                  />

                  <Chart
                    type="Line"
                    data={{ series: lineChartSeries }}
                    options={{
                      width: chartWidth,
                      height: "380px",
                      showPoint: false,
                      chartPadding: { left: 35, right: 75 },
                      axisX: { showGrid: false, showLabel: false },
                      axisY: {
                        scaleMinSpace: 30,
                        showGrid: false,
                        showLabel: false,
                      },
                      low: 0,
                      high: maxLineChartValue,
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Creates a useable chart from the server chart data.
function buildCharts(charts, keys, related, locals) {
  const result = _.map(charts, ({ group, by, series, data }) => {
    if (group === "undefined") group = "unassigned";
    return {
      title: group
        ? isFinite(group)
          ? Number(group)
          : _.startCase(group)
        : _.get(_.find(related.series, { value: series }), "title"),
      name: group || series,
      className: cx(series, by, group === "unassigned" ? "undefined" : group),
      data: _.map(keys, (key) =>
        related.dataMap(_.get(data, key), locals.query.units, locals)
      ),
    };
  });

  // Sort numerical groupings if needed.
  return _.isNumber(_.get(_.first(result), "title"))
    ? _.reverse(_.sortBy(result, "title"))
    : result;
}

function argsToArray(fn) {
  return function () {
    return fn(_.toArray(arguments));
  };
}

export default View;
